import { useEffect, useRef, useState } from 'react';
import { Listing } from 'types/Asset';

export type TimeCluster = 'PAST' | 'LIVE' | 'FUTURE';

const EXTRA_TIME_BEFORE_UPDATE = 5;

/**
 * Utility hook to cluster broadcast kind data by the time.
 *
 * If the broadcast is past clusters is as PAST
 * If the broadcast is live clusters is as LIVE and starts a timer to set it PAST when the live ends
 * If the broadcast is future clusters is as FUTURE and starts a timer to set it LIVE when the assets becomes live
 *
 * @param broadcast
 */
export const useBroadcastTimeCluster = (broadcast: Listing) => {
    const [cluster, setCluster] = useState<TimeCluster>(null);
    const updateClusterTimeoutRef = useRef<number>(null);

    const start = broadcast?.listings?.[0]?.start || broadcast?.start;
    const duration = broadcast?.listings?.[0]?.duration || broadcast?.duration;

    const isStartValid = start !== undefined && Number.isNaN(start) === false && Number.isFinite(start);
    const isDurationValid = duration !== undefined && Number.isNaN(duration) === false && Number.isFinite(duration);

    const startTime = isStartValid ? new Date(start * 1000) : broadcast?.startTime;
    const endTime = isDurationValid && isStartValid ? new Date((start + duration) * 1000) : broadcast?.endTime;

    const getTimestamp = (time: Date | number | undefined | null) => {
        if (!time) return undefined;

        if (typeof time === 'number') {
            return Number.isNaN(time) || !Number.isFinite(time) ? undefined : time;
        }

        if (time instanceof Date) {
            const timestamp = time.valueOf();
            return Number.isNaN(timestamp) ? undefined : timestamp;
        }

        return undefined;
    };

    const handleLiveToPast = () => {
        clearTimeout(updateClusterTimeoutRef.current);

        if (endTime) {
            const endTimeMs = getTimestamp(endTime);
            if (endTimeMs) {
                const timeUntilPast = endTimeMs - Date.now() + EXTRA_TIME_BEFORE_UPDATE;

                updateClusterTimeoutRef.current = window.setTimeout(() => {
                    setCluster('PAST');
                    clearTimeout(updateClusterTimeoutRef.current);
                }, timeUntilPast);
            }
        }
    };

    const handleFutureToLive = () => {
        clearTimeout(updateClusterTimeoutRef.current);

        if (startTime) {
            const startTimeMs = getTimestamp(startTime);
            if (startTimeMs) {
                const timeUntilLive = startTimeMs - Date.now() + EXTRA_TIME_BEFORE_UPDATE;

                updateClusterTimeoutRef.current = window.setTimeout(() => {
                    setCluster('LIVE');
                    clearTimeout(updateClusterTimeoutRef.current);
                }, timeUntilLive);
            }
        }
    };

    useEffect(() => {
        if (!broadcast) {
            return;
        }

        if (cluster && cluster !== 'PAST') {
            if (cluster === 'LIVE') {
                handleLiveToPast();
            } else {
                handleFutureToLive();
            }
        }
    }, [cluster, broadcast]);

    useEffect(() => {
        if (!broadcast) {
            return undefined;
        }

        const now = Date.now();
        const startMs = getTimestamp(startTime);
        const endMs = getTimestamp(endTime);

        if (endMs && endMs < now) {
            setCluster('PAST');
        } else if (startMs && endMs && startMs <= now && endMs >= now) {
            setCluster('LIVE');
        } else if (startMs && startMs > now) {
            setCluster('FUTURE');
        } else {
            setCluster('FUTURE');
        }

        return () => {
            clearTimeout(updateClusterTimeoutRef.current);
        };
    }, [broadcast]);

    return cluster;
};
